import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import {
  Card,
  CardProps,
  CardContent,
  CardHeader,
  CardActions,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Component variants using CVA
const cardVariants = cva(
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        elevated: 'shadow-md hover:shadow-lg',
        outlined: 'border border-gray-200 shadow-none hover:shadow-sm',
        filled: 'bg-gray-50 border-none shadow-none',
        flat: 'shadow-none border-none',
      },
      size: {
        xs: 'p-2',
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
        xl: 'p-8',
      },
      interactive: {
        true: 'cursor-pointer hover:scale-[1.02]',
        false: '',
      },
      collapsible: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'elevated',
      size: 'md',
      interactive: false,
      collapsible: false,
    },
  }
);

// Enhanced TypeScript interface
export interface SAPCardProps extends Omit<CardProps, 'variant'> {
  // Custom props
  variant?: 'elevated' | 'outlined' | 'filled' | 'flat';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  interactive?: boolean;
  collapsible?: boolean;
  
  // Header props
  title?: string;
  subtitle?: string;
  avatar?: React.ReactNode;
  action?: React.ReactNode;
  showActionMenu?: boolean;
  actionMenuItems?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  }>;
  
  // Content props
  children?: React.ReactNode;
  
  // Actions props
  actions?: React.ReactNode;
  showActions?: boolean;
  
  // Status props
  status?: 'default' | 'success' | 'warning' | 'error' | 'info';
  statusText?: string;
  
  // Badge props
  badge?: string | number;
  badgeColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  
  // Collapsible props
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
  
  // Custom styling
  customStyles?: React.CSSProperties;
  
  // Callbacks
  onClick?: () => void;
  onActionClick?: (action: string) => void;
}

// Styled components
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => 
    !['variant', 'size', 'interactive', 'collapsible', 'customStyles'].includes(prop as string),
})<SAPCardProps>(({ theme, variant = 'elevated', size = 'md', interactive, collapsible, customStyles }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: SAP_SPACING.component.borderRadius,
  border: variant === 'outlined' ? `1px solid ${SAP_COLORS.sapGray2}` : 'none',
  boxShadow: variant === 'elevated' ? SAP_SHADOWS.md : variant === 'outlined' ? 'none' : 'none',
  transition: SAP_TRANSITIONS.normal,
  overflow: 'hidden',
  
  '&:hover': {
    boxShadow: variant === 'elevated' ? SAP_SHADOWS.lg : variant === 'outlined' ? SAP_SHADOWS.sm : 'none',
    transform: interactive ? 'translateY(-2px)' : 'none',
  },
  
  ...customStyles,
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: `${SAP_SPACING.md} ${SAP_SPACING.lg}`,
  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
  
  '& .MuiCardHeader-title': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    fontSize: SAP_TYPOGRAPHY.fontSize.lg,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
  },
  
  '& .MuiCardHeader-subheader': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    marginTop: SAP_SPACING.xs,
  },
  
  '& .MuiCardHeader-action': {
    color: SAP_COLORS.sapGray5,
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: `${SAP_SPACING.lg} ${SAP_SPACING.lg}`,
  
  '&:last-child': {
    paddingBottom: SAP_SPACING.lg,
  },
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  padding: `${SAP_SPACING.md} ${SAP_SPACING.lg}`,
  borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
  backgroundColor: SAP_COLORS.sapGray1,
  justifyContent: 'flex-end',
  gap: SAP_SPACING.sm,
}));

const StatusIndicator = styled(Box)<{ status: string }>(({ theme, status }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: getStatusColor(status),
  marginRight: SAP_SPACING.sm,
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  backgroundColor: getStatusBackgroundColor(status),
  color: getStatusColor(status),
  border: `1px solid ${getStatusColor(status)}`,
  fontSize: SAP_TYPOGRAPHY.fontSize.xs,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  height: '24px',
}));

// Utility functions
function getStatusColor(status: string): string {
  switch (status) {
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    case 'error': return SAP_COLORS.sapRed;
    case 'info': return SAP_COLORS.sapBlue;
    default: return SAP_COLORS.sapGray4;
  }
}

function getStatusBackgroundColor(status: string): string {
  switch (status) {
    case 'success': return SAP_COLORS.sapLightGreen;
    case 'warning': return SAP_COLORS.sapLightOrange;
    case 'error': return SAP_COLORS.sapLightRed;
    case 'info': return SAP_COLORS.sapLightBlue;
    default: return SAP_COLORS.sapGray2;
  }
}

function getBadgeColor(badgeColor: string): string {
  switch (badgeColor) {
    case 'error': return SAP_COLORS.sapRed;
    case 'warning': return SAP_COLORS.sapOrange;
    case 'success': return SAP_COLORS.sapGreen;
    case 'info': return SAP_COLORS.sapBlue;
    case 'primary': return SAP_COLORS.sapBlue;
    case 'secondary': return SAP_COLORS.sapGray5;
    default: return SAP_COLORS.sapGray5;
  }
}

// Main component
export const SAPCard = forwardRef<HTMLDivElement, SAPCardProps>(
  (
    {
      variant = 'elevated',
      size = 'md',
      interactive = false,
      collapsible = false,
      title,
      subtitle,
      avatar,
      action,
      showActionMenu = false,
      actionMenuItems = [],
      children,
      actions,
      showActions = false,
      status,
      statusText,
      badge,
      badgeColor = 'primary',
      defaultExpanded = true,
      expanded,
      onToggleExpanded,
      customStyles,
      onClick,
      onActionClick,
      ...props
    },
    ref
  ) => {
    // Local state for collapsible
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const finalExpanded = expanded !== undefined ? expanded : isExpanded;

    // Handle toggle expanded
    const handleToggleExpanded = useCallback(() => {
      const newExpanded = !finalExpanded;
      setIsExpanded(newExpanded);
      onToggleExpanded?.(newExpanded);
    }, [finalExpanded, onToggleExpanded]);

    // Handle card click
    const handleClick = useCallback(() => {
      if (interactive && onClick) {
        onClick();
      }
    }, [interactive, onClick]);

    // Handle action click
    const handleActionClick = useCallback((actionLabel: string) => {
      onActionClick?.(actionLabel);
    }, [onActionClick]);

    // Render header action
    const renderHeaderAction = () => {
      if (action) return action;
      
      if (showActionMenu && actionMenuItems.length > 0) {
        return (
          <Tooltip title="More actions">
            <IconButton
              size="small"
              sx={{ color: SAP_COLORS.sapGray5 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        );
      }
      
      if (collapsible) {
        return (
          <IconButton
            size="small"
            onClick={handleToggleExpanded}
            sx={{ color: SAP_COLORS.sapGray5 }}
          >
            {finalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        );
      }
      
      return null;
    };

    // Render status
    const renderStatus = () => {
      if (!status) return null;
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', marginTop: SAP_SPACING.sm }}>
          <StatusIndicator status={status} />
          {statusText && (
            <Typography
              variant="caption"
              sx={{
                color: getStatusColor(status),
                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                fontSize: SAP_TYPOGRAPHY.fontSize.xs,
              }}
            >
              {statusText}
            </Typography>
          )}
        </Box>
      );
    };

    // Render badge
    const renderBadge = () => {
      if (!badge) return null;
      
      return (
        <Box sx={{ position: 'absolute', top: SAP_SPACING.sm, right: SAP_SPACING.sm }}>
          <Chip
            label={badge}
            size="small"
            sx={{
              backgroundColor: getBadgeColor(badgeColor),
              color: SAP_COLORS.sapWhite,
              fontSize: SAP_TYPOGRAPHY.fontSize.xs,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              height: '20px',
            }}
          />
        </Box>
      );
    };

    return (
      <StyledCard
        ref={ref}
        variant={variant === 'outlined' ? 'outlined' : 'elevation' as any}
        size={size}
        interactive={interactive}
        collapsible={collapsible}
        customStyles={customStyles}
        onClick={handleClick}
        sx={{
          position: 'relative',
          cursor: interactive ? 'pointer' : 'default',
          ...props.sx,
        }}
        {...props}
      >
        {/* Badge */}
        {renderBadge()}
        
        {/* Header */}
        {(title || subtitle || avatar) && (
          <StyledCardHeader
            avatar={avatar}
            title={title}
            subheader={subtitle}
            action={renderHeaderAction()}
          />
        )}
        
        {/* Status */}
        {renderStatus()}
        
        {/* Content */}
        <Collapse in={!collapsible || finalExpanded}>
          <StyledCardContent>
            {children}
          </StyledCardContent>
        </Collapse>
        
        {/* Actions */}
        {(showActions || actions) && (
          <StyledCardActions>
            {actions}
          </StyledCardActions>
        )}
      </StyledCard>
    );
  }
);

// Display name for debugging
SAPCard.displayName = 'SAPCard';

// Export types for external use
export { cardVariants };
